import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { buttonStyles } from '../css/commonStyles';
import {
  colors,
  fieldBlockStyles,
  fieldLabelStyles,
  filterToolbarSx,
  formFieldStyles,
  makeSelectSx,
  requiredSpanSx,
  selectMenuProps,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import {
  memberCancelBtnSx,
  memberConfirmBtnSx,
  memberDangerBtnSx,
  memberDialogBtnRowSx,
  memberDialogContentSx,
  memberDialogDescSx,
  memberDialogPaperSx,
  memberDialogTitleSx,
  memberDialogTitleWrapSx,
  memberFieldLabelSx,
  memberFieldsStackSx,
} from '../css/daoMemberModal.styles';
import MemberRowActions from './MemberRowActions';
import {
  cancelDaoMemberInvite,
  getDaoMembers,
  inviteDaoMember,
  reapproveDaoMember,
  resendDaoMemberInvite,
  reinviteDaoMember,
  reviewDaoMember,
  revokeDaoMember,
  updateDaoMember,
  updateDaoMemberSettings,
} from 'helpers/daoMember.api';
import { buildPaginationItems, formatDateTime, REVENUE_ITEMS_PAGE_SIZE } from '../utils/revenueItems.utils';
import { formatRevenueSharingErrorMessage } from '../utils/revenueSharing.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import PaymentsPagination from './PaymentsPagination';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'invited', label: 'Invited' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'declined', label: 'Declined' },
];

const MEMBER_STATUS_META = {
  approved: { label: 'Approved', color: '#4caf50' },
  pending_review: { label: 'Pending review', color: '#f59e0b' },
  invited: { label: 'Invited', color: '#3b82f6' },
  rejected: { label: 'Rejected', color: '#9aa4b2' },
  revoked: { label: 'Revoked', color: '#9aa4b2' },
  declined: { label: 'Declined', color: '#9aa4b2' },
};

const statusLabel = (status) => MEMBER_STATUS_META[status]?.label || status;

const MemberStatusBadge = ({ status }) => {
  const meta = MEMBER_STATUS_META[status] || {
    label: statusLabel(status),
    color: '#9aa4b2',
  };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '2px',
          bgcolor: meta.color,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          color: meta.color,
          fontFamily: 'Questrial, sans-serif',
          lineHeight: 1.4,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label}
      </Typography>
    </Box>
  );
};

const sourceLabel = (source) => {
  if (source === 'organizer_invite') return 'Organizer invite';
  if (source === 'self_request') return 'Self-request';
  return source || '—';
};

const shortWallet = (wallet) => {
  if (!wallet) return 'Not linked yet';
  if (wallet.length < 12) return wallet;
  return `${wallet.slice(0, 6)}…${wallet.slice(-4)}`;
};

const emptyInviteForm = { email: '', displayName: '', message: '' };
const emptyEditForm = { displayName: '' };
const TABLE_COL_COUNT = 6;

const DaoMembersSection = ({ currentDao, isOrganizer, walletAddress, onDaoRefresh }) => {
  const daoId = currentDao?._id;
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState({
    approved: 0,
    pending_review: 0,
    invited: 0,
    rejected: 0,
    revoked: 0,
    declined: 0,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: REVENUE_ITEMS_PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });
  const [allowSelfJoinRequest, setAllowSelfJoinRequest] = useState(!!currentDao?.allowSelfJoinRequest);
  const [savingSettings, setSavingSettings] = useState(false);
  const [modalActionKey, setModalActionKey] = useState(null);
  const [rowActionKey, setRowActionKey] = useState(null);
  const isModalBusy = Boolean(modalActionKey);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(emptyInviteForm);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    setAllowSelfJoinRequest(!!currentDao?.allowSelfJoinRequest);
  }, [currentDao?.allowSelfJoinRequest]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadMembers = useCallback(async () => {
    if (!daoId || !isOrganizer || !walletAddress) return;
    try {
      setLoading(true);
      const res = await getDaoMembers(
        daoId,
        {
          status: statusFilter || undefined,
          search: search || undefined,
          page,
          limit: REVENUE_ITEMS_PAGE_SIZE,
        },
        walletAddress,
      );
      setMembers(res?.data || []);
      setSummary(
        res?.summary || {
          approved: 0,
          pending_review: 0,
          invited: 0,
          rejected: 0,
          revoked: 0,
        },
      );
      setPagination({
        page: res?.pagination?.page || page,
        limit: res?.pagination?.limit || REVENUE_ITEMS_PAGE_SIZE,
        totalItems: res?.pagination?.totalItems || 0,
        totalPages: res?.pagination?.totalPages || 1,
      });
      if (typeof res?.dao?.allowSelfJoinRequest === 'boolean') {
        setAllowSelfJoinRequest(res.dao.allowSelfJoinRequest);
      }
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not load members.'));
      setMembers([]);
      setPagination({ page: 1, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [daoId, isOrganizer, page, search, statusFilter, walletAddress]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const summaryCards = useMemo(
    () => [
      { label: 'Approved', value: summary.approved },
      { label: 'Pending review', value: summary.pending_review },
      { label: 'Invited', value: summary.invited },
      { label: 'Revoked', value: summary.revoked },
    ],
    [summary],
  );

  const paginationItems = useMemo(
    () => buildPaginationItems(pagination.page || 1, pagination.totalPages || 1),
    [pagination.page, pagination.totalPages],
  );

  const paginationLabel = useMemo(() => {
    if (!pagination.totalItems) {
      return loading ? 'Loading members...' : 'No members found';
    }
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(start + members.length - 1, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} members`;
  }, [loading, members.length, pagination]);

  const handleToggleSelfJoin = async (event) => {
    const next = event.target.checked;
    try {
      setSavingSettings(true);
      await updateDaoMemberSettings(daoId, next, walletAddress);
      setAllowSelfJoinRequest(next);
      revenueSharingMessage.success(
        next ? 'Members can now request to join this DAO.' : 'Self-request to join is turned off.',
      );
      if (onDaoRefresh) onDaoRefresh();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not update join settings.'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email.trim()) {
      revenueSharingMessage.error('Please enter a platform email.');
      return;
    }
    try {
      setModalActionKey('invite');
      await inviteDaoMember(
        daoId,
        {
          email: inviteForm.email.trim(),
          displayName: inviteForm.displayName.trim() || undefined,
          message: inviteForm.message.trim() || undefined,
        },
        walletAddress,
      );
      revenueSharingMessage.success('Invitation sent. The member will receive an email link.');
      setInviteOpen(false);
      setInviteForm(emptyInviteForm);
      await loadMembers();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not send invitation.'));
    } finally {
      setModalActionKey(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedMember) return;
    try {
      setModalActionKey('edit');
      await updateDaoMember(
        daoId,
        selectedMember._id,
        {
          displayName: editForm.displayName.trim() || null,
        },
        walletAddress,
      );
      revenueSharingMessage.success('Member details saved.');
      setEditOpen(false);
      setSelectedMember(null);
      await loadMembers();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not save member details.'));
    } finally {
      setModalActionKey(null);
    }
  };

  const handleReview = async (decision) => {
    if (!selectedMember) return;
    try {
      setModalActionKey(decision);
      await reviewDaoMember(
        daoId,
        selectedMember._id,
        {
          decision,
          note: reviewNote.trim() || undefined,
        },
        walletAddress,
      );
      revenueSharingMessage.success(
        decision === 'approve' ? 'Member approved.' : 'Membership request rejected.',
      );
      setReviewOpen(false);
      setReviewNote('');
      setSelectedMember(null);
      await loadMembers();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not complete review.'));
    } finally {
      setModalActionKey(null);
    }
  };

  const handleRevoke = async () => {
    if (!selectedMember) return;
    try {
      setModalActionKey('revoke');
      await revokeDaoMember(
        daoId,
        selectedMember._id,
        {
          reason: revokeReason.trim() || undefined,
        },
        walletAddress,
      );
      revenueSharingMessage.success('Member revoked. They can no longer be selected for new work in this DAO.');
      setRevokeOpen(false);
      setRevokeReason('');
      setSelectedMember(null);
      await loadMembers();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not revoke member.'));
    } finally {
      setModalActionKey(null);
    }
  };

  const runRowAction = async (actionKey, action, successMessage) => {
    try {
      setRowActionKey(actionKey);
      await action();
      revenueSharingMessage.success(successMessage);
      await loadMembers();
    } catch (error) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Could not complete this action.'));
    } finally {
      setRowActionKey(null);
    }
  };

  const buildMemberActions = (member) => {
    const actions = [];
    const id = member._id;

    if (member.status === 'invited') {
      actions.push({
        key: `${id}:resend`,
        label: 'Resend',
        onClick: () =>
          runRowAction(
            `${id}:resend`,
            () => resendDaoMemberInvite(daoId, id, walletAddress),
            'Invitation email resent.',
          ),
      });
      actions.push({
        key: `${id}:cancel`,
        label: 'Cancel invite',
        onClick: () =>
          runRowAction(
            `${id}:cancel`,
            () => cancelDaoMemberInvite(daoId, id, walletAddress),
            'Invitation cancelled.',
          ),
      });
    }

    if (member.status === 'pending_review') {
      actions.push({
        key: `${id}:review`,
        label: 'Review',
        onClick: () => {
          if (rowActionKey) return;
          setSelectedMember(member);
          setReviewNote('');
          setReviewOpen(true);
        },
      });
    }

    if (['approved', 'pending_review', 'invited'].includes(member.status)) {
      actions.push({
        key: `${id}:edit`,
        label: 'Edit',
        onClick: () => {
          if (rowActionKey) return;
          setSelectedMember(member);
          setEditForm({
            displayName: member.displayName || '',
          });
          setEditOpen(true);
        },
      });
    }

    if (member.status === 'approved') {
      actions.push({
        key: `${id}:revoke`,
        label: 'Revoke',
        onClick: () => {
          if (rowActionKey) return;
          setSelectedMember(member);
          setRevokeReason('');
          setRevokeOpen(true);
        },
      });
    }

    if (member.status === 'revoked') {
      actions.push({
        key: `${id}:reapprove`,
        label: 'Re-approve',
        onClick: () =>
          runRowAction(
            `${id}:reapprove`,
            () => reapproveDaoMember(daoId, id, walletAddress),
            'Member re-approved.',
          ),
      });
    }

    if (member.status === 'declined') {
      actions.push({
        key: `${id}:reinvite`,
        label: 'Re-invite',
        onClick: () =>
          runRowAction(
            `${id}:reinvite`,
            () => reinviteDaoMember(daoId, id, walletAddress),
            'Invitation sent again.',
          ),
      });
    }

    return actions;
  };

  if (!isOrganizer) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        DAO Members
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <FormControlLabel
          sx={{ m: 0, mr: { md: 2 } }}
          control={
            <Switch
              checked={allowSelfJoinRequest}
              onChange={handleToggleSelfJoin}
              disabled={savingSettings}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent },
              }}
            />
          }
          label={
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
              Allow people to request joining this DAO
            </Typography>
          }
        />
        <Button
          variant="contained"
          onClick={() => {
            setInviteForm(emptyInviteForm);
            setInviteOpen(true);
          }}
          sx={{
            ...buttonStyles,
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'center' },
            minHeight: 40,
            px: 2.5,
            borderRadius: '10px',
            textTransform: 'none',
            fontFamily: 'Questrial, sans-serif',
            fontSize: '0.95rem',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              ...buttonStyles['&:hover'],
              boxShadow: 'none',
            },
          }}
        >
          + Invite member
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {summaryCards.map((card) => (
          <Box
            key={card.label}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              background: colors.panel,
            }}
          >
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>{card.label}</Typography>
            <Typography sx={{ color: colors.textPrimary, fontSize: '1.5rem', mt: 0.5 }}>{card.value}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={filterToolbarSx}>
        <Box sx={{ ...fieldBlockStyles, flex: 1, minWidth: 220, maxWidth: 420 }}>
          <Typography sx={fieldLabelStyles}>Search</Typography>
          <TextField
            placeholder="Email, name, or wallet"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            fullWidth
            sx={formFieldStyles}
          />
        </Box>
        <Box sx={{ ...fieldBlockStyles, minWidth: 160 }}>
          <Typography sx={fieldLabelStyles}>Status</Typography>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            displayEmpty
            fullWidth
            renderValue={(v) => {
              if (!v) return 'All statuses';
              return statusLabel(v);
            }}
            sx={makeSelectSx(false, !!statusFilter)}
            MenuProps={selectMenuProps}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Wallet</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_COUNT} align="center">
                    <CircularProgress size={24} sx={{ color: colors.accent, my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_COUNT} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    No members yet. Invite someone to get started.
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member._id}>
                    <TableCell>{member.displayName || member.email}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {shortWallet(member.walletAddress)}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{sourceLabel(member.source)}</TableCell>
                    <TableCell>
                      <MemberStatusBadge status={member.status} />
                    </TableCell>
                    <TableCell>
                      <MemberRowActions actions={buildMemberActions(member)} activeKey={rowActionKey} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaymentsPagination
          pagination={pagination}
          paginationItems={paginationItems}
          paginationLabel={paginationLabel}
          onPageChange={setPage}
        />
      </Box>

      <Dialog
        open={inviteOpen}
        onClose={() => !isModalBusy && setInviteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Invite DAO member
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Typography sx={memberDialogDescSx}>
            Invite a platform user to join this DAO as DAO Member.
          </Typography>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>
                Platform email{' '}
                <Box component="span" sx={requiredSpanSx}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                placeholder="new.member@example.com"
                value={inviteForm.email}
                onChange={(e) => {
                  const email = e.target.value;
                  setInviteForm((prev) => ({ ...prev, email }));
                }}
                sx={formFieldStyles}
              />
            </Box>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Display name (optional)</Typography>
              <TextField
                fullWidth
                placeholder="Sam (Sales)"
                value={inviteForm.displayName}
                onChange={(e) => {
                  const displayName = e.target.value;
                  setInviteForm((prev) => ({ ...prev, displayName }));
                }}
                sx={formFieldStyles}
              />
            </Box>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Message (optional)</Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Join this DAO as a member"
                value={inviteForm.message}
                onChange={(e) => {
                  const message = e.target.value;
                  setInviteForm((prev) => ({ ...prev, message }));
                }}
                sx={formFieldStyles}
              />
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={isModalBusy} onClick={() => setInviteOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isModalBusy && modalActionKey !== 'invite'}
              onClick={handleInvite}
              sx={memberConfirmBtnSx}
            >
              {modalActionKey === 'invite' ? (
                <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
              ) : (
                'Send invitation'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => !isModalBusy && setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Edit member
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Typography sx={{ ...memberDialogDescSx, display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {selectedMember?.displayName || selectedMember?.email || 'Member'} ·{' '}
            <MemberStatusBadge status={selectedMember?.status} />
          </Typography>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Display name</Typography>
              <TextField
                fullWidth
                value={editForm.displayName}
                onChange={(e) => {
                  const displayName = e.target.value;
                  setEditForm((prev) => ({ ...prev, displayName }));
                }}
                sx={formFieldStyles}
              />
            </Box>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Email</Typography>
              <TextField
                fullWidth
                value={selectedMember?.email || ''}
                disabled
                sx={formFieldStyles}
              />
            </Box>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Linked wallet</Typography>
              <TextField
                fullWidth
                value={selectedMember?.walletAddress || 'Not linked yet'}
                disabled
                sx={formFieldStyles}
              />
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={isModalBusy} onClick={() => setEditOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isModalBusy && modalActionKey !== 'edit'}
              onClick={handleSaveEdit}
              sx={memberConfirmBtnSx}
            >
              {modalActionKey === 'edit' ? (
                <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
              ) : (
                'Save changes'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reviewOpen}
        onClose={() => !isModalBusy && setReviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { ...memberDialogPaperSx, maxWidth: 680 } }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Review member request
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Typography sx={memberDialogDescSx}>
            {selectedMember?.displayName || selectedMember?.email || 'This user'} requested to join as a DAO
            member.
          </Typography>
          <Box
            sx={{
              mb: 2.5,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              overflow: 'hidden',
            }}
          >
            {[
              ['Submitted', formatDateTime(selectedMember?.updatedAt || selectedMember?.createdAt)],
              ['Email', selectedMember?.email],
              ['Linked wallet', selectedMember?.walletAddress || 'Not linked yet'],
              ['Source', sourceLabel(selectedMember?.source)],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${colors.border}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ ...memberFieldLabelSx, minWidth: 120, flexShrink: 0 }}>{label}</Typography>
                <Typography
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: 'Questrial, sans-serif',
                    fontSize: '0.9rem',
                    wordBreak: label === 'Linked wallet' ? 'normal' : 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Note to member (optional)</Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                sx={formFieldStyles}
              />
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button
              disabled={isModalBusy && modalActionKey !== 'reject'}
              onClick={() => handleReview('reject')}
              sx={memberCancelBtnSx}
            >
              {modalActionKey === 'reject' ? (
                <CircularProgress size={20} sx={{ color: colors.textPrimary }} />
              ) : (
                'Reject'
              )}
            </Button>
            <Button
              variant="contained"
              disabled={isModalBusy && modalActionKey !== 'approve'}
              onClick={() => handleReview('approve')}
              sx={memberConfirmBtnSx}
            >
              {modalActionKey === 'approve' ? (
                <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
              ) : (
                'Approve member'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={revokeOpen}
        onClose={() => !isModalBusy && setRevokeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Revoke member access?
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Typography sx={memberDialogDescSx}>
            Remove <Box component="strong" sx={{ color: colors.textPrimary }}>{selectedMember?.displayName || selectedMember?.email || 'this member'}</Box> from
            approved membership for this DAO.
          </Typography>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>Reason (optional)</Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                sx={formFieldStyles}
              />
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={isModalBusy} onClick={() => setRevokeOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isModalBusy && modalActionKey !== 'revoke'}
              onClick={handleRevoke}
              sx={memberDangerBtnSx}
            >
              {modalActionKey === 'revoke' ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Revoke member'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DaoMembersSection;
